import { useEffect, useState, useRef } from "react";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import { IncomeExpenseSummarySkeleton } from "../../Components/SkeletonLoading";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
} from "../../Components/Styles/PageStyles";
import { useNavigate } from "react-router-dom";
import tw from "tailwind-styled-components";
import { IoChevronBackOutline } from "react-icons/io5";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import moment from "moment";
import { IoIosArrowForward } from "react-icons/io";

const dueTypeReverseMap = Object.fromEntries(
    Object.entries(Config.dueType).map(([k, v]) => [v, k.replaceAll("_", " ")])
);
const getMonthDateRange = (month, year) => {
    const startDate = moment({ year, month: month - 1 })
        .startOf("month")
        .format("YYYY-MM-DD");

    const endDate = moment({ year, month: month - 1 })
        .endOf("month")
        .format("YYYY-MM-DD");

    return { startDate, endDate };
};

const IncomeAndExpenseDetails = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty
    );
    const properties = useSelector((state) => state.PropertyReducer.property);
    const [selectedProperty, setSelectedProperty] = useState(property);
    const [apiData, setApiData] = useState([]);
    const [securityInHand, setSecurityInHand] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalTitle, setModalTitle] = useState("");
    const modalRef = useRef(null);

    const fetchFunction = async () => {
        let url = "client/pendingrent/month/daily";
        if (selectedProperty.id !== 0) {
            url = `property/pendingrent/month/daily/${selectedProperty.id}`
        }
        const response = await axios.get(
            `${Config.apiUrl}/${url}`,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess) {
            setApiData(res.data?.data || []);
            setSecurityInHand(res.securityInHand || 0);
        }
    };

    const getError = () => { };

    const { isLoading, mutate: getListMutate, error, isRefetching } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        if (selectedProperty) {
            const handler = setTimeout(() => {
                getListMutate();
            }, 200);
            return () => clearTimeout(handler);
        }
    }, [selectedProperty, getListMutate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setModalOpen(false);
            }
        };
        if (modalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [modalOpen]);

    const openModal = (title, data) => {
        setModalTitle(title);
        setModalData(data);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalData(null);
    };

    const handleBackClick = () => {
        navigate("/dashboard");
    };

    const handlePropertySelect = (property) => {
        setSelectedProperty(property);
    };
    return (
        <BoxContainer>
            <BoxHead >
                <BoxTitle className="!mb-6 whitespace-nowrap">
                    <BackWrapper>
                        <BackButton onClick={handleBackClick}>
                            <IoChevronBackOutline size={20} />
                        </BackButton>
                        <div >
                            <span className="flex items-center"> Income & Expense Summary</span>
                            <Underline />
                        </div>
                    </BackWrapper>
                </BoxTitle>

                <PropertiesFilterDropdown
                    properties={properties}
                    selectedProperty={selectedProperty}
                    onPropertySelect={handlePropertySelect}
                    placeholder="All Properties"
                    className="w-full md:w-[200px]"
                />
            </BoxHead>

            {(isLoading || isRefetching) && <IncomeExpenseSummarySkeleton />}

            {!error && !isLoading && (
                <div className="flex flex-col gap-8">
                    {apiData.map((item, idx) => (
                        <MonthlyFinancialCard
                            key={`${item.year}-${item.month}`}
                            item={item}
                            selectedProperty={selectedProperty}
                            navigate={navigate}
                            openModal={openModal}
                        />
                    ))}
                </div>
            )}

            {
                modalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
                        <div
                            ref={modalRef}
                            className="bg-white rounded-2xl w-full max-w-md shadow-lg p-6 relative animate-fadeIn"
                        >
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                                {modalTitle}
                            </h2>

                            {modalData && Object.keys(modalData).length > 0 ? (
                                <div className="max-h-60 overflow-y-auto space-y-2">
                                    {Object.entries(modalData).map(([key, value]) => {
                                        const label =
                                            dueTypeReverseMap[key] || key;
                                        return (
                                            <div
                                                key={key}
                                                className="flex justify-between items-center border-b border-gray-100 py-1"
                                            >
                                                <span className="text-gray-600 text-sm">{label}</span>
                                                <span className="text-gray-800 font-medium text-sm">
                                                    ₹{Number(value).toLocaleString()}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-sm">No data available.</p>
                            )}

                            <button
                                onClick={closeModal}
                                className="mt-5 w-full bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 text-white rounded-lg py-2 transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )
            }
        </BoxContainer >
    );
};

const MonthlyFinancialCard = ({
    item,
    selectedProperty,
    navigate,
    openModal,
}) => {
    const profit = item.netProfitLoss;
    const profitPercent =
        item.totalIncome > 0
            ? ((profit / item.totalIncome) * 100).toFixed(1)
            : 0;

    return (
        <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden">
            {/* HEADER */}
            <div className="bg-gray-100 px-5 py-4 flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                        {getMonthName(item.month)} {item.year}
                    </h2>
                    <p className="text-xs text-gray-600">
                        {item.dueTenantCount} tenants have pending dues
                    </p>
                </div>

                <button
                    onClick={() =>
                        navigate(
                            `/daily-summary/${selectedProperty.id}?m=${item.month}&y=${item.year}`
                        )
                    }
                    className="text-xs text-gray-600 hover:text-primary-color"
                >
                    Day-wise →
                </button>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5">
                <FinanceCard title="Total Income" value={item.totalIncome} tone="success" />
                <FinanceCard title="Total Expense" value={item.totalExpense} tone="danger" />
                <FinanceCardWithSubLabel
                    title={profit >= 0 ? "Net Profit" : "Net Loss"}
                    value={profit}
                    subValue={profitPercent + '%'}
                    tone={profit >= 0 ? "success" : "danger"}
                />
                <FinanceCard title="Pending Dues" value={item.totalDues} tone="warning"
                    clickable
                    onClick={() => {
                        const { startDate, endDate } =
                            getMonthDateRange(item.month, item.year);
                        sessionStorage.setItem("due_startDate", startDate)
                        sessionStorage.setItem("due_endDate", endDate)
                        navigate(
                            `/finance/dues`
                        );
                    }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-5 pb-5">
                <SummaryCard title="Income Breakdown">
                    <Row label="Rent Received" value={item.totalRent} />
                    <Row label="Security" value={item.totalSecurity} />
                    <Row
                        label="Other Income"
                        value={item.otherIncome}
                        info={() =>
                            openModal("Other Income Details", item.otherOnDate)
                        }
                    />
                    <Divider />
                    <Row label="Total Income" value={item.totalIncome} bold />
                </SummaryCard>

                <SummaryCard title="Expense Breakdown">
                    <Row label="Rent & Utilities" value={item.totalBuildingRentExpense} />
                    <Row label="Food / Kitchen" value={item.totalFoodExpense} />
                    <Row label="Staff / Salary" value={item.totalStaffSalaryExpense} />
                    <Row
                        label="Other Expenses"
                        value={item.otherExpense}
                        info={() =>
                            openModal("Other Expense Details", item.otherExpenseOnDate)
                        }
                    />
                    <Divider />
                    <Row label="Total Expense" value={item.totalExpense} bold />
                </SummaryCard>
            </div>

            {/* COLLECTION SUMMARY */}
            <div className="px-5 pb-5">
                <SummaryCard title="Collection Summary">
                    <Row label="Online Payments" value={item.totalOnline} />
                    <Row label="Cash Received" value={item.totalOffline} />
                    <Row label="Kipinn Payments" value={item.totalPayu} />
                    <Divider />
                    <Row
                        label="Total Collected"
                        value={item.totalOnline + item.totalOffline + item.totalPayu}
                        bold
                    />
                </SummaryCard>
            </div>

            {/* SECURITY ADJUSTMENT SUMMARY */}
            {item.totalSecurityAdjusted > 0 && (
                <div className="px-5 pb-5">
                    <SummaryCard title="Security Deposit Adjustment">
                        <Row
                            label="Adjusted Against Dues"
                            value={item.totalSecurityAdjusted}
                            bold
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Security deposit adjusted against rent or charges.
                        </p>
                    </SummaryCard>
                </div>
            )}

        </div>
    );
};

const FinanceCard = ({ title, value, tone, onClick, clickable }) => {
    const toneMap = {
        success: "text-green-700 bg-green-50",
        danger: "text-red-700 bg-red-50",
        warning: "text-orange-700 bg-orange-50",
    };

    return (
        <div onClick={clickable ? onClick : undefined}
            className={`group rounded-2xl border p-4 ${toneMap[tone] || ""} ${clickable
                ? "cursor-pointer "
                : ""
                }`}
        >
            <p className="text-xs text-gray-500">{title}</p>
            <MetricValue>
                <p className="text-2xl font-semibold">
                    ₹{Math.abs(value || 0).toLocaleString()}

                </p>
                {clickable && <Arrow />}
            </MetricValue>
        </div>
    );
};

const FinanceCardWithSubLabel = ({ title, value, tone, subValue }) => {
    const toneMap = {
        success: "text-green-700 bg-green-50",
        danger: "text-red-700 bg-red-50",
        warning: "text-orange-700 bg-orange-50",
    };

    return (
        <div className={`rounded-2xl border p-4 ${toneMap[tone] || ""}`}>
            <p className="text-xs text-gray-500">{title}</p>
            <p className="text-2xl font-semibold">
                ₹{Math.abs(value || 0).toLocaleString()}
                <span className="text-xs"> {"(" + subValue + ")"} </span>
            </p>
        </div>
    );
};

const SummaryCard = ({ title, children }) => (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {title}
        </h3>
        <div className="space-y-2">{children}</div>
    </div>
);

const Row = ({ label, value, bold, info }) => (
    <div className="flex justify-between items-center text-sm">
        <span className={`text-gray-600 ${bold ? "font-semibold" : ""}`}>
            {label}
            {info && (
                <button
                    onClick={info}
                    className="ml-1 text-blue-600 text-xs underline"
                >
                    details
                </button>
            )}
        </span>
        <span className={`text-gray-800 ${bold ? "font-semibold" : ""}`}>
            ₹{Number(value || 0).toLocaleString()}
        </span>
    </div>
);

const Divider = () => (
    <div className="border-t border-gray-200 my-2" />
);

const getMonthName = (month) => {
    return moment(month, "MM").format("MMMM");
};

const BackWrapper = tw.div`flex items-center`;
const BackButton = tw.button`hover:bg-gray-200 items-center -mt-1 p-1 rounded-md transition-colors`;
const Arrow = tw(IoIosArrowForward)`
  text-gray-400 text-xs
  opacity-0 group-hover:opacity-100
  transition-opacity
`;
const MetricValue = tw.div`
  flex items-center gap-1
  text-lg font-semibold
  transition-colors
`;
export default IncomeAndExpenseDetails;

